import type { SuburbInput } from "@repo/adapter-core";
import { buildQueryPaginatedUrl, slugify } from "@repo/adapter-core";
import type { DomainAdapterConfig } from "./config.ts";

const BASE_URL = "https://www.domain.com.au";

const LISTING_TYPE_PATHS: Record<string, string> = {
  buy: "/sale/",
  rent: "/rent/",
  sold: "/sold-listings/",
};

const TIME_RANGE_DAYS: Record<string, string> = {
  "last-7-days": "7",
  "last-30-days": "30",
  "last-90-days": "90",
  "last-year": "365",
  "last-2-years": "730",
  "last-5-years": "1825",
};

export function buildSearchUrl(suburb: SuburbInput, config: DomainAdapterConfig): string {
  const listingType = config.listingType || "buy";
  const basePath = LISTING_TYPE_PATHS[listingType] || LISTING_TYPE_PATHS.buy;
  const url = `${BASE_URL}${basePath}${slugify(suburb.locality)}-${suburb.state.toLowerCase()}-${suburb.postcode}/`;
  const params = new URLSearchParams();
  const propertyTypes = config.propertyTypes ?? [];

  if (propertyTypes.length > 0) {
    params.set("ptype", propertyTypes.join(","));
  }

  if (config.minBedrooms !== undefined) {
    params.set(
      "bedrooms",
      config.maxBedrooms !== undefined
        ? `${config.minBedrooms}-${config.maxBedrooms}`
        : `${config.minBedrooms}-any`,
    );
  }

  if (config.minBathrooms !== undefined) {
    params.set("bathrooms", `${config.minBathrooms}-any`);
  }

  if (config.minParking !== undefined) {
    params.set("carspaces", `${config.minParking}-any`);
  }

  if (config.minPrice !== undefined || config.maxPrice !== undefined) {
    const min = config.minPrice ?? "";
    const max = config.maxPrice ?? "";
    params.set("price", `${min}-${max}`);
  }

  if (listingType === "sold") {
    // Prefer the named timeRange when present (validated values). When a job
    // uses absolute dates instead, `effectiveMaxAgeDays` is the computed
    // window — pick the smallest Domain bucket that covers it so we
    // still narrow the result set in-URL (Domain only honors the named
    // soldwithin buckets — 7/30/90/180/365/730/1825/3650).
    let soldwithinDays: string | undefined;
    if (config.timeRange) {
      soldwithinDays = TIME_RANGE_DAYS[config.timeRange];
    } else if (typeof config.effectiveMaxAgeDays === "number") {
      const d = config.effectiveMaxAgeDays;
      if (d <= 7) soldwithinDays = "7";
      else if (d <= 30) soldwithinDays = "30";
      else if (d <= 90) soldwithinDays = "90";
      else if (d <= 180) soldwithinDays = "180";
      else if (d <= 365) soldwithinDays = "365";
      else if (d <= 730) soldwithinDays = "730";
      else if (d <= 1825) soldwithinDays = "1825";
      else soldwithinDays = "3650";
    }
    if (soldwithinDays) {
      params.set("soldwithin", soldwithinDays);
    }
    if (config.excludePriceWithheld) {
      params.set("excludepricewithheld", "1");
    }
    params.set("sort", config.sortBy || "solddate-desc");
  } else {
    params.set("sort", config.sortBy || "dateupdated-desc");
  }

  if (config.excludeSurroundingSuburbs) {
    params.set("ssubs", "0");
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export function buildPaginatedUrl(searchUrl: string, page: number): string {
  return buildQueryPaginatedUrl(searchUrl, page);
}
